/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { L10nLocale, L10nTranslationService, L10nTranslatePipe, L10N_LOCALE } from 'angular-l10n';
import { MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatPaginator } from '@angular/material/paginator';
import { MatButton } from '@angular/material/button';
import { Association } from '../../hickory-api';
import { AssociationType, PagedAssociationValues } from '../association-types/association-type';
import { LoadingComponent } from '../../shared/loading/loading.component';
import { Observable } from 'rxjs';

/**
 * Modal dialog component for selecting values from an association type.
 *
 * This component displays a searchable, paginated list of available values for a given
 * association (e.g., all assets, all groups, all users) and allows the user to select
 * multiple values by checkbox. Selected values are added to the association's values array.
 *
 * @example
 * // Used within associations.component.html
 * <app-select-association-values
 *   [association]="editAssociation"
 *   [valuesService]="editService"
 *   [showModal]="showModalAssociationEvent"
 *   (updated)="onUpdated($event)"
 *   (done)="onDismissModal()">
 * </app-select-association-values>
 *
 * @see AssociationType for the service that provides available values
 * @see Association for the data structure being modified
 */
@Component({
  selector: 'app-select-association-values',
  templateUrl: './select-association-values.component.html',
  styleUrls: ['./select-association-values.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    MatDialogTitle, CdkScrollable, MatDialogContent, MatDialogActions, MatDialogClose,
    MatFormField, MatLabel, MatPrefix, MatInput, MatIcon,
    MatCheckbox, MatPaginator, MatButton,
    L10nTranslatePipe, LoadingComponent,
  ],
})
export class SelectAssociationValuesComponent implements OnInit {
  readonly locale = inject<L10nLocale>(L10N_LOCALE);
  private readonly translation = inject(L10nTranslationService);

  @Input() set association(association: Association) {
    this._association = association;
    this.values = null;
  }
  @Input() set valuesService(value: AssociationType) {
    this._valuesService = value;
    if (value) {
      this.updateValues();
    }
  }
  @Input() showButton: boolean = true;
  @Input() showModal?: Observable<void>;
  @Output() updated = new EventEmitter<Association>();
  @Output() done = new EventEmitter<void>();

  titleOptions: Record<string, string> = {
    'Asset.Id': 'AssetIdTitle',
    'Asset.ExternalId': 'AssetExternalIdTitle',
    'Group.Id': 'Association.Filter.Groups',
    'Group.ExternalId': 'Association.Filter.Groups',
    'User.Id': 'Association.Filter.Users',
    'User.ExternalId': 'Association.Filter.Users'
  };

  _association: Association = null;
  get association() {
    return this._association;
  }

  _valuesService: AssociationType;
  get valuesService() {
    return this._valuesService;
  }

  get title(): string {
    const titleId = this.titleOptions[this.association.key];
    return titleId ? this.translation.translate(titleId) : this.association.key.slice(this.association.key.indexOf('.') + 1);
  }
  
  loadFailed: boolean;
  values: any[];
  search: string = '';
  page: number = 1;
  pageSize: number = 10;
  count: number = 0;

  ngOnInit() {
    // listen for when this modal is (re-)displayed and clear the "search" value
    this.showModal?.subscribe(() => this.search = '');
  }

  apply() {
    this.updated.emit(this.association);
    this.done.emit();
    this.search = '';
    return false;
  }

  onSearchChanged(matchString: string) {
    this.search = matchString;
    this.page = 1;
    this.updateValues();
  }

  onPageChanged(page: number) {
    this.page = page;
    this.updateValues();
  }

  updateValues() {
    this.loadFailed = false;
    this.valuesService.getValues(this.page, this.pageSize, this.search).subscribe({
      next: (p: PagedAssociationValues) => {
        this.values = p.items;
        this.count = p.totalItemCount;
      },
      error: _err => {
        this.loadFailed = true;
      }
    });
  }

  manageValues(value: string) {
    const idx = this.association.values.indexOf(value);
    if (idx === -1) {
      this.association.values.push(value);
    } else {
      this.association.values.splice(idx, 1);
    }
    this.updated.emit(this.association);
  }

  selectAll(values: any[]) {
    const selected = this.association.values;
    values.forEach(function (v) {
      if (!(selected).includes(v.value)) {
        selected.push(v.value);
      }
    });
    this.association.values = selected;
    this.updated.emit(this.association);
  }

  clearPage(values: any[]) {
    this.search= '';
    const selected = this.association.values;
    values.forEach(function (v) {
      for (let i = 0, len = selected.length; i < len; i++) {
        if (selected[i] === v.value) {
          selected.splice(i, 1);
          break;
        }
      }
    });
    this.association.values = selected;
    this.updated.emit(this.association);
  }

  isSelectedCheck(value: string) {
    return this.association.values.includes(value);
  }
}
